import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { 
  Download, Loader2, Video, X, ArrowLeft, 
  Camera, Sparkles, Check, RefreshCw,
  Zap, Upload, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Footer from '../Footer';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ReelsWithAvatarsPage = () => {
  const navigate = useNavigate();
  const imageInputRef = useRef(null);
  
  // Simple state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [motionPrompt, setMotionPrompt] = useState('');
  const [selectedModel, setSelectedModel] = useState('high-quality');
  const [duration, setDuration] = useState(5);
  
  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [resultVideoUrl, setResultVideoUrl] = useState(null);
  const [error, setError] = useState(null);

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        toast.error('Please upload JPG, PNG, or WebP image');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image too large. Maximum 10MB.');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      toast.success('Image uploaded!');
    }
  };

  // Generate video
  const generateVideo = async () => {
    if (!imageFile) {
      toast.error('Please upload an image');
      return;
    }
    
    if (!motionPrompt.trim()) {
      toast.error('Please describe the motion you want');
      return;
    }
    
    setIsGenerating(true);
    setProgress(0);
    setError(null);
    setResultVideoUrl(null);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      formData.append('image', imageFile);
      formData.append('prompt', motionPrompt);
      formData.append('model', selectedModel);
      formData.append('duration', duration.toString());
      
      const response = await axios.post(`${API}/creator-ai/clone/generate`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000
      });
      
      if (response.data.success) {
        setJobId(response.data.job_id);
        toast.success('Video generation started!');
        pollStatus(response.data.job_id);
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to start generation');
      setIsGenerating(false);
      toast.error(error.response?.data?.detail || 'Failed to start generation');
    }
  };

  // Poll job status
  const pollStatus = async (id) => {
    const token = localStorage.getItem('token');
    let attempts = 0;
    const maxAttempts = 120;
    
    const poll = async () => {
      if (attempts >= maxAttempts) {
        setError('Generation timed out. Please try again.');
        setIsGenerating(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/creator-ai/clone/status/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success) {
          const { status, progress: jobProgress, video_url, error: jobError } = response.data;
          
          setProgress(jobProgress || 0);
          
          if (status === 'completed' && video_url) {
            setResultVideoUrl(video_url);
            setIsGenerating(false);
            toast.success('Your video is ready!');
            return;
          } else if (status === 'failed') {
            setError(jobError || 'Generation failed');
            setIsGenerating(false);
            toast.error(jobError || 'Generation failed');
            return;
          }
        }
        
        attempts++;
        setTimeout(poll, 5000);
      } catch (error) {
        console.error('Poll error:', error);
        attempts++;
        setTimeout(poll, 5000);
      }
    };
    
    poll();
  };

  // Reset to start
  const resetAll = () => {
    setImageFile(null);
    setImagePreview(null);
    setMotionPrompt('');
    setSelectedModel('high-quality');
    setJobId(null);
    setProgress(0);
    setResultVideoUrl(null);
    setError(null);
    setIsGenerating(false);
  };

  const modelOptions = [
    { value: 'high-quality', label: 'Standard Quality', desc: 'Great for most use cases' },
    { value: 'professional', label: 'Enhanced Quality', desc: 'Better details & clarity' },
    { value: 'cinematic', label: 'Premium Quality', desc: 'Highest quality animations' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/image-generator')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-3"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Back to Creator AI</span>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Video className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Image to Video Creation</h1>
              <p className="text-sm text-gray-500">Transform your images into animated videos</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
          <CardContent className="p-6 sm:p-8">
            
            {/* Show result if video is ready */}
            {resultVideoUrl ? (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full mb-4">
                    <Check className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-700">Your Video is Ready!</span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                    Download Your AI Video
                  </h2>
                </div>

                {/* Video Preview */}
                <Card className="overflow-hidden">
                  <div className="aspect-video max-h-[500px] bg-black flex items-center justify-center">
                    <video
                      src={resultVideoUrl}
                      controls
                      className="h-full w-full"
                      autoPlay
                      loop
                    />
                  </div>
                  <CardContent className="p-4">
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="px-3 py-1 bg-cyan-100 text-cyan-700 text-xs rounded-full">HD Quality</span>
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">AI Generated</span>
                    </div>
                    
                    <Button
                      onClick={() => window.open(resultVideoUrl, '_blank')}
                      className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:opacity-90"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download Video
                    </Button>
                  </CardContent>
                </Card>

                {/* Actions */}
                <div className="flex justify-center">
                  <Button variant="outline" onClick={resetAll}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Create Another Video
                  </Button>
                </div>
              </div>
            ) : isGenerating ? (
              /* Generating state */
              <div className="space-y-8 text-center max-w-lg mx-auto">
                <div>
                  <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center animate-pulse">
                    <Video className="h-12 w-12 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Creating Your Video
                  </h2>
                  <p className="text-gray-600">Please wait while we animate your image...</p>
                </div>

                <div className="space-y-2">
                  <Progress value={progress} className="h-3" />
                  <p className="text-sm text-gray-500">{progress}% complete</p>
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-600">
                    Using AI to transform your image into an animated video
                  </p>
                </div>

                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-red-700">{error}</p>
                  </div>
                )}
              </div>
            ) : (
              /* Input form */
              <div className="space-y-6">
                {/* Hero */}
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-100 to-blue-100 rounded-full mb-4">
                    <Sparkles className="h-4 w-4 text-cyan-600" />
                    <span className="text-sm font-medium text-cyan-700">Simple Image-to-Video</span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                    Transform Your Image into Video
                  </h2>
                  <p className="text-gray-600 max-w-md mx-auto">
                    Upload an image, describe the motion, and choose animation quality
                  </p>
                </div>

                {/* Image Upload */}
                <div className="max-w-md mx-auto">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">1. Upload Your Image</label>
                  <Card 
                    className={`border-2 border-dashed cursor-pointer transition-all hover:border-cyan-400 hover:bg-cyan-50/30 ${
                      imagePreview ? 'border-green-400 bg-green-50/30' : 'border-gray-200'
                    }`}
                    onClick={() => imageInputRef.current?.click()}
                  >
                    <CardContent className="p-8 text-center">
                      <input
                        ref={imageInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      
                      {imagePreview ? (
                        <div className="relative">
                          <img 
                            src={imagePreview} 
                            alt="Your image" 
                            className="w-full max-h-64 mx-auto rounded-lg object-cover border-4 border-cyan-400 shadow-lg" loading="lazy" decoding="async"/>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setImageFile(null);
                              setImagePreview(null);
                            }}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 shadow-lg"
                          >
                            <X className="h-4 w-4" />
                          </button>
                          <p className="mt-4 text-green-600 font-medium">Image ready!</p>
                        </div>
                      ) : (
                        <>
                          <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full flex items-center justify-center">
                            <ImageIcon className="h-10 w-10 text-cyan-600" />
                          </div>
                          <h3 className="font-semibold text-gray-900 text-lg mb-2">Click to upload image</h3>
                          <p className="text-gray-500">JPG, PNG, or WebP (max 10MB)</p>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Motion Prompt */}
                <div className="max-w-md mx-auto">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">2. Describe the Motion</label>
                  <textarea
                    value={motionPrompt}
                    onChange={(e) => setMotionPrompt(e.target.value)}
                    placeholder="Example: Person smiling and waving at camera, camera slowly pans right"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-cyan-400 focus:outline-none resize-none"
                    rows="4"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Tip: Describe camera movements and actions for best results
                  </p>
                </div>

                {/* Model Selection */}
                <div className="max-w-md mx-auto">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">3. Choose Animation Quality</label>
                  <div className="space-y-2">
                    {modelOptions.map((model) => (
                      <div
                        key={model.value}
                        onClick={() => setSelectedModel(model.value)}
                        className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                          selectedModel === model.value
                            ? 'border-cyan-500 bg-cyan-50'
                            : 'border-gray-200 hover:border-cyan-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{model.label}</p>
                            <p className="text-sm text-gray-500">{model.desc}</p>
                          </div>
                          {selectedModel === model.value && (
                            <Check className="h-5 w-5 text-cyan-600" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Duration Selection */}
                <div className="max-w-md mx-auto">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">4. Video Duration</label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="3"
                      max="5"
                      step="1"
                      value={duration}
                      onChange={(e) => setDuration(parseInt(e.target.value))}
                      className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-cyan-600"
                    />
                    <div className="text-center min-w-[60px]">
                      <p className="text-2xl font-bold text-cyan-600">{duration}</p>
                      <p className="text-xs text-gray-500">seconds</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    ⏱️ Standard video length is 3-5 seconds
                  </p>
                </div>

                {/* Generate Button */}
                <div className="flex justify-center pt-4">
                  <Button
                    onClick={generateVideo}
                    disabled={!imageFile || !motionPrompt.trim()}
                    className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:opacity-90 px-8 py-6 text-lg"
                  >
                    <Zap className="h-5 w-5 mr-2" />
                    Generate Video
                  </Button>
                </div>

                {/* Info */}
                <div className="text-center text-sm text-gray-500 space-y-1">
                  <p>💳 100 credits per video</p>
                  <p>⏱️ Processing time: 1-3 minutes</p>
                  <p>📹 Video duration: 3-5 seconds</p>
                </div>
              </div>
            )}
            
          </CardContent>
        </Card>
      </div>

      <div className="mb-24"></div>
      <Footer />
    </div>
  );
};

export default ReelsWithAvatarsPage;
